import React, { useState, useMemo } from 'react';
import { Modal, Spin, Tag, Button, message } from 'antd';
import { ClockCircleOutlined, UndoOutlined } from '@ant-design/icons';
import useVersionHistory from 'hooks/treatment/useVersionHistory';
import { updateTreatmentTemplate } from 'api/videoSetting';
import { EAutoSaveBusinessType, IAutoSaveInfo } from 'types/treatment';
import { autoSaveInfo } from 'api/treatment';
import dayjs from 'dayjs';
import s from './s.module.less';

interface TemplateHistoryModalProps {
    open: boolean;
    onCancel: () => void;
    templateId: number;
    onRestoreSuccess?: () => void;
    onBack: () => void;
}

/** 解析 autoSaveInfo.content：存储格式为 JSON.stringify({ name, content }) */
interface ParsedContent {
    name: string;
    content: string;
}

const parseAutoSaveContent = (raw: string): ParsedContent | null => {
    try {
        const parsed = JSON.parse(raw) as { name?: string; content?: string };
        if (typeof parsed?.content === 'string') {
            return {
                name: typeof parsed.name === 'string' ? parsed.name : '',
                content: parsed.content,
            };
        }
    } catch {
        // 兼容非 JSON 格式的旧数据
    }
    return null;
};

const CONTENT_PREVIEW_LENGTH = 80;

const stripHtml = (html: string): string =>
    html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

const formatDate = (dateStr: string): string => {
    const date = dayjs(dateStr);
    if (date.isSame(dayjs(), 'day')) return 'Today';
    return date.format('dddd, MMMM D, YYYY');
};

/** 将 IAutoSaveInfo 转为可展示的条目，过滤无法解析的记录 */
const toHistoryEntry = (item: IAutoSaveInfo, index: number) => {
    const parsed = parseAutoSaveContent(item.content);
    if (!parsed) return null;
    const dateStr = item.updateAt || item.createAt || '';
    return {
        key: item.id ?? `v-${index}`,
        date: dateStr,
        isCurrent: index === 0,
        name: parsed.name,
        content: parsed.content,
    };
};

const TemplateHistoryModal = ({
    open,
    onCancel,
    templateId,
    onRestoreSuccess,
    onBack,
}: TemplateHistoryModalProps) => {
    const [expandedIds, setExpandedIds] = useState<Set<string | number>>(new Set());

    const [data, loading] = useVersionHistory({
        businessId: templateId,
        businessType: EAutoSaveBusinessType.TREATMENT_PLAN_TEMPLATE_CONTENT,
        autoFetch: open && !!templateId,
    });

    const historyEntries = useMemo(
        () => data.map((item, i) => toHistoryEntry(item, i)).filter((e): e is NonNullable<typeof e> => e !== null),
        [data],
    );

    const handleRestore = (entry: { key: string | number; name: string; content: string }) => {
        Modal.confirm({
            title: 'Restore this version',
            content:
                'This will replace the current template with this version. This action cannot be undone.',
            okText: 'Restore',
            onOk: async () => {
                if (!templateId) return;
                try {
                    const result = await updateTreatmentTemplate(templateId, {
                        name: entry.name,
                        content: entry.content,
                    });
                    if (!result?.error) {
                        message.success('Template restored successfully');
                        autoSaveInfo({
                            autoSaveBusinessType: EAutoSaveBusinessType.TREATMENT_PLAN_TEMPLATE_CONTENT,
                            businessId: templateId,
                            content: JSON.stringify({ name: entry.name, content: entry.content }),
                        });
                        onRestoreSuccess?.();
                        onCancel();
                    }
                } catch (err) {
                    console.error('Failed to restore template:', err);
                }
            },
            onCancel: () => { },
            icon: null,
            width: 500,
            closable: true,
            wrapClassName: s.customConfirmRoot,
        });
    };

    const toggleExpand = (key: string | number) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const renderContentField = (entry: { key: string | number; content: string }) => {
        const plainText = stripHtml(entry.content);
        const isExpanded = expandedIds.has(entry.key);
        const isLong = plainText.length > CONTENT_PREVIEW_LENGTH;

        if (!isLong || isExpanded) {
            return (
                <div
                    className={s.fieldContent}
                    dangerouslySetInnerHTML={{ __html: entry.content }}
                />
            );
        }

        return (
            <div className={s.fieldValue}>
                {plainText.slice(0, CONTENT_PREVIEW_LENGTH)} ...{' '}
                <button
                    type="button"
                    className={s.showAllLink}
                    onClick={() => toggleExpand(entry.key)}
                >
                    Show all
                </button>
            </div>
        );
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            footer={null}
            width={800}
            className={s.modal}
            title="Version history"
            destroyOnClose
        >
            <Spin spinning={loading}>
                <Button type="link" onClick={onBack} className={s.backButton}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M6.66667 12.6654L2 7.9987M2 7.9987L6.66667 3.33203M2 7.9987L14 7.9987" stroke="#0E62F4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    Back
                </Button>
                <div className={s.body}>
                    {historyEntries.length === 0 && !loading && (
                        <div className={s.emptyState}>No version history yet</div>
                    )}
                    {historyEntries.map((entry, index) => (
                        <React.Fragment key={entry.key}>
                            {index > 0 && <div className={s.divider} />}
                            <div className={s.historyEntry}>
                                <div className={s.entryHeader}>
                                    <div className={s.dateRow}>
                                        <ClockCircleOutlined className={s.clockIcon} />
                                        <span className={s.dateText}>{formatDate(entry.date)}</span>
                                        {entry.isCurrent && (
                                            <Tag className={s.currentBadge}>Current</Tag>
                                        )}
                                    </div>
                                    {!entry.isCurrent && (
                                        <Button
                                            className={s.restoreButton}
                                            onClick={() => handleRestore(entry)}
                                            icon={<UndoOutlined />}
                                        >
                                            Restore
                                        </Button>
                                    )}
                                </div>
                                <div className={s.field}>
                                    <div className={s.fieldLabel}>Name</div>
                                    <div className={s.fieldValue}>{entry.name}</div>
                                </div>
                                <div className={s.field}>
                                    <div className={s.fieldLabel}>Content</div>
                                    {renderContentField(entry)}
                                </div>
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            </Spin>
        </Modal>
    );
};

export default TemplateHistoryModal;
